import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Plus, Trash2, Power, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { Button } from '../../../components/ui/button';
import PortalLayout from '../../../components/PortalLayout';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import axios from 'axios';

const EmailRecipientsManagement = () => {
  const { token } = useAuth();
  const toast = useToast();
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ email: '', name: '', is_active: true });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, action: null, title: '', message: '' });
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchRecipients();
  }, []);

  const fetchRecipients = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/email-recipients/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRecipients(response.data.recipients);
    } catch (error) {
      toast.error('Error loading email recipients');
    } finally {
      setLoading(false);
    }
  };

  const createRecipient = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/email-recipients/`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email recipient added successfully');
      setShowModal(false);
      setFormData({ email: '', name: '', is_active: true });
      fetchRecipients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error adding recipient');
    }
  };

  const deleteRecipient = (recipientId, recipientName) => {
    setConfirmDialog({
      isOpen: true,
      action: () => confirmDelete(recipientId),
      title: 'Delete Email Recipient?',
      message: `Are you sure you want to delete ${recipientName}?\n\nThey will no longer receive lead notifications.`
    });
  };

  const confirmDelete = async (recipientId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/email-recipients/${recipientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Recipient deleted successfully');
      fetchRecipients();
    } catch (error) {
      toast.error('Error deleting recipient');
    }
  };

  const toggleRecipient = async (recipientId, currentStatus, name) => {
    try {
      await axios.put(`${BACKEND_URL}/api/email-recipients/${recipientId}`,
        { is_active: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${name} ${currentStatus ? 'deactivated' : 'activated'}`);
      fetchRecipients();
    } catch (error) {
      toast.error('Error updating recipient status');
    }
  };

  return (
    <PortalLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Email Notification Recipients</h1>
            <p className="text-gray-600">Manage who receives email notifications when new leads arrive</p>
          </div>
          <Button onClick={() => setShowModal(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-5 h-5 mr-2" />Add Recipient
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {recipients.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-12 text-center text-gray-500">
                        <Mail className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                        <p className="text-lg font-medium">No email recipients configured</p>
                        <p className="text-sm">Add recipients to receive notifications when new leads arrive</p>
                      </td>
                    </tr>
                  ) : (
                    recipients.map((recipient) => (
                      <tr key={recipient.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="font-medium text-gray-900">{recipient.name}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-gray-700">{recipient.email}</div>
                        </td>
                        <td className="px-6 py-4">
                          {recipient.is_active ? (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircle className="w-4 h-4 mr-1" />Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              <XCircle className="w-4 h-4 mr-1" />Inactive
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => toggleRecipient(recipient.id, recipient.is_active, recipient.name)}
                              className={`p-2 rounded-lg transition-colors ${recipient.is_active ? 'hover:bg-orange-50' : 'hover:bg-green-50'}`}
                              title={recipient.is_active ? 'Deactivate' : 'Activate'}
                            >
                              <Power className={`w-5 h-5 ${recipient.is_active ? 'text-orange-600' : 'text-green-600'}`} />
                            </button>
                            <button
                              onClick={() => deleteRecipient(recipient.id, recipient.name)}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 className="w-5 h-5 text-red-600" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Add Recipient Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-xl p-8 max-w-md w-full">
              <h2 className="text-2xl font-bold mb-6">Add Email Recipient</h2>
              <form onSubmit={createRecipient} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Name</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Email Address</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="john@ascarii.com"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="w-5 h-5 text-blue-600 rounded"
                  />
                  <label className="text-sm text-gray-700">Active (receives notifications)</label>
                </div>
                <div className="flex gap-3 mt-6">
                  <Button type="button" onClick={() => setShowModal(false)} variant="outline" className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                    Add Recipient
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={confirmDialog.isOpen}
          onClose={() => setConfirmDialog({ isOpen: false, action: null, title: '', message: '' })}
          onConfirm={confirmDialog.action}
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
        />
      </div>
    </PortalLayout>
  );
};

export default EmailRecipientsManagement;
